import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { ISegmentDefinition, MailrailAutoSendEmailCampaign } from '@feathr/blackbox';
import { getSegmentsUniqueEmailsPayload, transformTriggersToPredicates } from '@feathr/blackbox';
import type { IRadioOption } from '@feathr/components';
import { Alert, Radios } from '@feathr/components';
import { useUniqueEmailsCount } from '@feathr/extender/hooks';

interface IProps {
  campaign: MailrailAutoSendEmailCampaign;
  disabled: boolean;
}

function MailrailBulkSendConfig({ campaign, disabled }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const selectedOption = campaign.get('bulk_send_enabled')
    ? campaign.get('bulk_send_timing')
    : 'none';
  const alreadyDidBulkSend = campaign.hasStarted && selectedOption === 'start';
  const canConfigureEndBulkSend = campaign.isDraft || campaign.isScheduled;
  const isTimeSubtype = campaign.get('subtype') === 'time';
  const includedSegmentIds = campaign.get('segments_included', []).filter(Boolean);

  const previouslyMatchedCount = useUniqueEmailsCount(
    (Segments) => {
      const predicates = campaign.get('predicates');
      if (!predicates.length) {
        return null;
      }
      // An ephemeral segment filtering the target audience to persons who already satisfied the
      // campaign's trigger predicates
      const triggerPredicateSegment: ISegmentDefinition = {
        lookback_mode: 'unbounded',
        mode: campaign.get('predicates_mode') ?? 'match_any',
        predicates: transformTriggersToPredicates(toJS(predicates)),
      };
      const payload = getSegmentsUniqueEmailsPayload(Segments, campaign);
      return {
        ...payload,
        // The trigger filter narrows the included groups when there are any; with no included
        // groups it is itself the audience.
        ...(payload.included_segment_ids?.length || payload.included_segments?.length
          ? { filter_segment: triggerPredicateSegment }
          : { included_segments: [triggerPredicateSegment] }),
      };
    },
    [campaign],
  );

  useEffect(() => {
    // A resolved count of 0 means no contacts have previously matched the triggers, so an
    // at-start bulk send would send to no one. An undefined count is unknown (still loading or
    // the request failed) and must not turn off a saved bulk send.
    if (previouslyMatchedCount === 0 && selectedOption === 'start') {
      campaign.set({ bulk_send_enabled: false });
    }
  }, [previouslyMatchedCount]);

  function handleBulkSendChange(newValue?: string): void {
    if (newValue === 'none') {
      campaign.set({ bulk_send_enabled: false });
    } else if (newValue === 'start' || newValue === 'end') {
      campaign.set({ bulk_send_enabled: true, bulk_send_timing: newValue });
    }
  }

  function getRadiosHelpText(): string {
    if (campaign.isMailrailDrip) {
      return t('Choose whether to include a bulk enrollment at the beginning of the campaign.');
    }
    return t('Choose whether to include a bulk send at the beginning or end of the campaign.');
  }

  function getNoneOptionTooltip(): string {
    if (alreadyDidBulkSend) {
      return t(
        'This option is unavailable because the campaign already performed a bulk send when it started.',
      );
    }
    if (campaign.isMailrailDrip) {
      return t(
        'No bulk enrollment. Your target audience will only be enrolled if and when they satisfy the enrollment trigger.',
      );
    }
    return t(
      'No bulk send will be performed. Your target audience will only be emailed if and when they perform the trigger.',
    );
  }

  function getStartOptionTooltip(): string {
    if (campaign.hasStarted && selectedOption !== 'start') {
      return t('This option is unavailable because the campaign has already started.');
    }
    if (!previouslyMatchedCount) {
      return t(
        'This option is unavailable because there are no contacts in your audience who have previously matched your selected triggering filters.',
      );
    }
    if (campaign.isMailrailDrip) {
      return t(
        'At the start of the campaign, {{ count, number }} recipients who have previously matched your selected enrollment triggers will be enrolled immediately.',
        { count: previouslyMatchedCount },
      );
    }
    return t(
      'At the start of the campaign, {{ count, number }} recipients who have previously matched your selected triggering filters will be emailed immediately.',
      { count: previouslyMatchedCount },
    );
  }

  function getEndOptionTooltip(): string {
    if (alreadyDidBulkSend) {
      return t(
        'This option is unavailable because the campaign already performed a bulk send when it started.',
      );
    }
    if (!canConfigureEndBulkSend) {
      return t('An end-of-campaign bulk send can only be added before the campaign starts.');
    }
    if (!includedSegmentIds.length && campaign.get('repeat_allowed')) {
      return t(
        'This option is unavailable because it requires at least one included group and cannot be combined with the "Send every time" cadence.',
      );
    }
    if (!includedSegmentIds.length) {
      return t('This option is unavailable because it requires at least one included group.');
    }
    if (campaign.get('repeat_allowed')) {
      return t(
        'This option is unavailable because it cannot be combined with the "Send every time" cadence.',
      );
    }
    return t(
      'At the end of the campaign, the email will be sent to ALL the contacts in your targeted group(s) who have not yet received it. This option is only available before the start of the campaign.',
    );
  }

  const bulkSendOptions: IRadioOption[] = [
    {
      id: 'none',
      name: t('None'),
      disabled: disabled || alreadyDidBulkSend,
      tooltip: getNoneOptionTooltip(),
    },
    {
      id: 'start',
      name: t('At start time'),
      disabled: disabled || campaign.hasStarted || isTimeSubtype || !previouslyMatchedCount,
      tooltip: getStartOptionTooltip(),
    },
    // Drips only support an at-start (enrollment) bulk send
    ...(campaign.isMailrailDrip
      ? []
      : [
          {
            id: 'end',
            name: t('At end time'),
            disabled:
              disabled ||
              !canConfigureEndBulkSend ||
              alreadyDidBulkSend ||
              isTimeSubtype ||
              !includedSegmentIds.length ||
              campaign.get('repeat_allowed'),
            tooltip: getEndOptionTooltip(),
          },
        ]),
  ];

  return (
    <>
      <Radios
        dataName={'bulk_send_options'}
        helpText={getRadiosHelpText()}
        label={campaign.isMailrailDrip ? t('Bulk enrollment') : t('Bulk send')}
        layout={'block'}
        onChange={handleBulkSendChange}
        options={bulkSendOptions}
        value={selectedOption}
      />
      {selectedOption === 'end' && (
        <Alert
          description={t(
            'If you include an end-of-campaign bulk send, you will not be able to re-publish your campaign after it reaches its end date.',
          )}
          title={t('End-of-campaign bulk send is final')}
          type={'info'}
        />
      )}
    </>
  );
}

export default observer(MailrailBulkSendConfig);
